import { inject } from '@angular/core';
import { Auth, user } from '@fido/firebase/auth';
import { doc, Firestore, getDocFromServer } from '@fido/firebase/firestore';
import { CanActivateFn, Router } from '@angular/router';
import { OrgRole, orgMemberPath } from 'fido-types';
import { firstValueFrom } from 'rxjs';
import { appUserConverter } from '../../auth/data/app-user';

export const roleGuard =
  (allowedRoles: OrgRole[]): CanActivateFn =>
  async () => {
    const router = inject(Router);
    const auth = inject(Auth);
    const firestore = inject(Firestore);

    const u = await firstValueFrom(user(auth));

    if (!u) return router.createUrlTree(['/sign-in']);

    const appUser = await getDocFromServer(
      doc(firestore, 'users', u.uid).withConverter(appUserConverter),
    );
    // Missing user doc: transient sync race or deleted doc — redirect to
    // /onboarding rather than throwing an uncaught navigation error.
    if (!appUser.exists()) return router.createUrlTree(['/onboarding']);

    const organizationId = appUser.data().organizationId;
    if (!organizationId) return router.createUrlTree(['/onboarding']);

    // Check role from membership document
    const memberDoc = await getDocFromServer(doc(firestore, orgMemberPath(organizationId, u.uid)));
    if (!memberDoc.exists()) return router.createUrlTree(['/']);

    const role = memberDoc.data()?.['role'];
    const status = memberDoc.data()?.['status'];

    if (role && status === 'active' && allowedRoles.includes(role)) return true;

    return router.createUrlTree(['/']);
  };
